import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

//Include Both Helper File with needed methods
import {
  getProducts as getProductsApi,
  deleteProducts as deleteProductsApi,
  getOrders as getOrdersApi,
  getSellers as getSellersApi,
  getSeller as getSellerApi,
  addNewSeller as addNewSellerApi,
  updateSeller as updateSellerApi,
  deleteSeller as deleteSellerApi,
  suspendSeller as suspendSellerApi,
  getCustomers as getCustomersApi,
  updateOrder as updateOrderApi,
  deleteOrder as deleteOrderApi,
  addNewOrder as addNewOrderApi,
  addNewCustomer as addNewCustomerApi,
  updateCustomer as updateCustomerApi,
  deleteCustomer as deleteCustomerApi,
  addNewProduct as addNewProductApi,
  updateProduct as updateProductApi,
  getEcommerceCategories as getCategoriesApi,
  addNewEcommerceCategory as addNewCategoryApi,
  updateEcommerceCategory as updateCategoryApi,
  deleteEcommerceCategory as deleteCategoryApi,
  getSubcategories as getSubcategoriesApi,
  addNewSubcategory as addNewSubcategoryApi,
  updateSubcategory as updateSubcategoryApi,
  deleteSubcategory as deleteSubcategoryApi,
  getAttributes as getAttributesApi,
  addNewAttribute as addNewAttributeApi,
  updateAttribute as updateAttributeApi,
  deleteAttribute as deleteAttributeApi,
  getCommissions as getCommissionsApi,
  addNewCommission as addNewCommissionApi,
  updateCommission as updateCommissionApi,
  deleteCommission as deleteCommissionApi,
  getTaxes as getTaxesApi,
  addNewTax as addNewTaxApi,
  updateTax as updateTaxApi,
  deleteTax as deleteTaxApi
} from "../../api";

export const getProducts = createAsyncThunk("ecommerce/getProducts", async (params = {}) => {
  try {
    const response = await getProductsApi(params);
    return response;
  } catch (error) {
    return error;
  }
});

export const getOrders = createAsyncThunk("ecommerce/getOrders", async () => {
  try {
    const response = getOrdersApi();
    return response;
  } catch (error) {
    return error;
  }
});

export const getSellers = createAsyncThunk("ecommerce/getSellers", async () => {
  try {
    const response = await getSellersApi();
    return response;
  } catch (error) {
    return error;
  }
});

export const getSeller = createAsyncThunk("ecommerce/getSeller", async (sellerId) => {
  try {
    const response = await getSellerApi(sellerId);
    return response;
  } catch (error) {
    return error;
  }
});

export const addNewSeller = createAsyncThunk("ecommerce/addNewSeller", async (seller, { dispatch, rejectWithValue }) => {
  try {
    const response = await addNewSellerApi(seller);
    const data = response?.data || response;
    toast.success("Seller Added Successfully", { autoClose: 3000 });
    
    // Refresh sellers list
    dispatch(getSellers());
    
    return data;
  } catch (error) {
    console.error("Error adding seller:", error);
    toast.error("Seller Added Failed", { autoClose: 3000 });
    return rejectWithValue({ error: error?.message || "Failed to add seller" });
  }
});

export const updateSeller = createAsyncThunk("ecommerce/updateSeller", async (seller, { dispatch, rejectWithValue }) => {
  try {
    const response = await updateSellerApi(seller);
    const data = response?.data || response;
    toast.success("Seller Updated Successfully", { autoClose: 3000 });
    
    // Refresh sellers list
    dispatch(getSellers());
    
    return data;
  } catch (error) {
    console.error("Error updating seller:", error);
    toast.error("Seller Update Failed", { autoClose: 3000 });
    return rejectWithValue({ error: error?.message || "Failed to update seller" });
  }
});

export const deleteSeller = createAsyncThunk("ecommerce/deleteSeller", async (sellerId, { dispatch }) => {
  try {
    const response = await deleteSellerApi(sellerId);
    toast.success("Seller Deleted Successfully", { autoClose: 3000 });
    
    // Refresh sellers list
    dispatch(getSellers());
    
    return { seller: sellerId, ...response };
  } catch (error) {
    toast.error("Seller Delete Failed", { autoClose: 3000 });
    return error;
  }
});

export const suspendSeller = createAsyncThunk("ecommerce/suspendSeller", async ({ sellerId, status }, { dispatch }) => {
  try {
    const response = await suspendSellerApi(sellerId, status);
    toast.success(`Seller ${status === 'suspended' ? 'Suspended' : status === 'active' ? 'Activated' : 'Status Updated'} Successfully`, { autoClose: 3000 });
    
    // Refresh sellers list
    dispatch(getSellers());
    
    return response;
  } catch (error) {
    toast.error("Seller Status Update Failed", { autoClose: 3000 });
    return error;
  }
});

export const getCustomers = createAsyncThunk("ecommerce/getCustomers", async () => {
  try {
    const response = await getCustomersApi();
    return response;
  } catch (error) {
    return error;
  }
});

export const deleteProducts = createAsyncThunk("ecommerce/deleteProducts", async (product, { dispatch }) => {
  try {
    const response = deleteProductsApi(product);
    toast.success("Product Deleted Successfully", { autoClose: 3000 });
    
    // Refresh products list
    dispatch(getProducts());
    
    return { product, ...response };
  } catch (error) {
    toast.error("Product Delete Failed", { autoClose: 3000 });
    return error;
  }
});

export const updateOrder = createAsyncThunk("ecommerce/updateOrder", async (order) => {
  try {
    const response = updateOrderApi(order);
    const data = await response;
    toast.success("Order Updateded Successfully", { autoClose: 3000 });
    return data;
  } catch (error) {
    toast.error("Order Updateded Failed", { autoClose: 3000 });
    return error;
  }
});

export const addNewProduct = createAsyncThunk("ecommerce/addNewProduct", async (product, { dispatch, rejectWithValue }) => {
  try {
    const response = await addNewProductApi(product);
    const data = response?.data || response;
    toast.success("Product Added Successfully", { autoClose: 3000 });
    
    // Refresh products list - get user info for filtering
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getProducts(params));
    
    return data;
  } catch (error) {
    console.error("Error adding product:", error);
    // Show specific error message from backend if available
    const errorMessage = error?.error || error?.message || "Failed to add product";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const updateProduct = createAsyncThunk("ecommerce/updateProduct", async (product, { dispatch, rejectWithValue }) => {
  try {
    const response = await updateProductApi(product);
    const data = response?.data || response;
    toast.success("Product Updated Successfully", { autoClose: 3000 });
    
    // Refresh products list - get user info for filtering
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getProducts(params));
    
    return data;
  }
  catch (error) {
    console.error("Error updating product:", error);
    // Show specific error message from backend if available
    const errorMessage = error?.error || error?.message || "Failed to update product";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const deleteOrder = createAsyncThunk("ecommerce/deleteOrder", async (order) => {
  try {
    const response = deleteOrderApi(order);
    toast.success("Order Deleted Successfully", { autoClose: 3000 });
    return { order, ...response };
  } catch (error) {
    toast.error("Order Deleted Failed", { autoClose: 3000 });
    return error;
  }
});

export const addNewOrder = createAsyncThunk("ecommerce/addNewOrder", async (order) => {
  try {
    const response = addNewOrderApi(order);
    const data = await response;
    toast.success("Order Added Successfully", { autoClose: 3000 });
    return data;
  } catch (error) {
    toast.error("Order Added Failed", { autoClose: 3000 });
    return error;
  }
});

export const updateCustomer = createAsyncThunk("ecommerce/updateCustomer", async (customer) => {
  try {
    const response = updateCustomerApi(customer);
    const data = await response;
    toast.success("Customer Updateded Successfully", { autoClose: 3000 });
    return data;
  } catch (error) {
    toast.error("Customer Updateded Failed", { autoClose: 3000 });
    return error;
  }
});

export const deleteCustomer = createAsyncThunk("ecommerce/deleteCustomer", async (customer) => {
  try {
    const response = deleteCustomerApi(customer);
    toast.success("Customer Deleted Successfully", { autoClose: 3000 });
    return { customer, ...response }
  } catch (error) {
    toast.error("Customer Deleted Failed", { autoClose: 3000 });
    return error;
  }
});

export const addNewCustomer = createAsyncThunk("ecommerce/addNewCustomer", async (customer) => {
  try {
    const response = addNewCustomerApi(customer);
    const data = await response;
    toast.success("Customer Added Successfully", { autoClose: 3000 });
    return data;
  } catch (error) {
    toast.error("Customer Added Failed", { autoClose: 3000 });
    return error;
  }
});

// Categories
export const getEcommerceCategories = createAsyncThunk("ecommerce/getCategories", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await getCategoriesApi(params);
    return response;
  } catch (error) {
    console.error("Error fetching categories:", error);
    // Handle different error formats
    const errorData = error?.response?.data || error?.message || error;
    return rejectWithValue({
      error: typeof errorData === 'string' ? errorData : (errorData?.error || errorData?.message || "Failed to load categories"),
      message: typeof errorData === 'string' ? errorData : (errorData?.message || errorData?.error || "Failed to load categories"),
      originalError: error
    });
  }
});

export const addNewEcommerceCategory = createAsyncThunk("ecommerce/addNewCategory", async (category, { dispatch, getState, rejectWithValue }) => {
  try {
    const response = await addNewCategoryApi(category);
    const data = response?.data || response;
    toast.success("Category Added Successfully", { autoClose: 3000 });
    
    // Refresh categories list
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getEcommerceCategories(params));
    
    return data;
  } catch (error) {
    console.error("Error adding category:", error);
    const errorMessage = error?.error || error?.message || "Category Add Failed";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const updateEcommerceCategory = createAsyncThunk("ecommerce/updateCategory", async (category, { dispatch, getState, rejectWithValue }) => {
  try {
    const response = await updateCategoryApi(category);
    const data = response?.data || response;
    toast.success("Category Updated Successfully", { autoClose: 3000 });
    
    // Refresh categories list
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getEcommerceCategories(params));
    
    return data;
  } catch (error) {
    console.error("Error updating category:", error);
    const errorMessage = error?.error || error?.message || "Category Update Failed";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const deleteEcommerceCategory = createAsyncThunk("ecommerce/deleteCategory", async (categoryId, { dispatch, getState, rejectWithValue }) => {
  try {
    const response = await deleteCategoryApi(categoryId);
    toast.success("Category Deleted Successfully", { autoClose: 3000 });

    // Refresh categories list
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getEcommerceCategories(params));

    return { categoryId, ...response };
  } catch (error) {
    const message = error?.message || error?.error || "Category delete failed.";
    toast.error(message, { autoClose: 5000 });
    return rejectWithValue(error);
  }
});

// Subcategories
export const getSubcategories = createAsyncThunk("ecommerce/getSubcategories", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await getSubcategoriesApi(params);
    return response;
  } catch (error) {
    return rejectWithValue(error?.response?.data || error?.message || error);
  }
});

export const addNewSubcategory = createAsyncThunk("ecommerce/addNewSubcategory", async (subcategory, { dispatch, getState, rejectWithValue }) => {
  try {
    const response = await addNewSubcategoryApi(subcategory);
    const data = response?.data || response;
    toast.success("Subcategory Added Successfully", { autoClose: 3000 });
    
    // Refresh subcategories list
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getSubcategories(params));
    
    return data;
  } catch (error) {
    console.error("Error adding subcategory:", error);
    const errorMessage = error?.error || error?.message || "Subcategory Add Failed";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const updateSubcategory = createAsyncThunk("ecommerce/updateSubcategory", async (subcategory, { dispatch, getState, rejectWithValue }) => {
  try {
    const response = await updateSubcategoryApi(subcategory);
    const data = response?.data || response;
    toast.success("Subcategory Updated Successfully", { autoClose: 3000 });
    
    // Refresh subcategories list
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getSubcategories(params));
    
    return data;
  } catch (error) {
    console.error("Error updating subcategory:", error);
    const errorMessage = error?.error || error?.message || "Subcategory Update Failed";
    toast.error(errorMessage, { autoClose: 5000 });
    return rejectWithValue({ error: errorMessage, details: error });
  }
});

export const deleteSubcategory = createAsyncThunk("ecommerce/deleteSubcategory", async (subcategoryId, { dispatch, getState }) => {
  try {
    const response = await deleteSubcategoryApi(subcategoryId);
    toast.success("Subcategory Deleted Successfully", { autoClose: 3000 });
    
    // Refresh subcategories list
    const state = getState();
    const userProfile = JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || userProfile.data?.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getSubcategories(params));
    
    return { subcategoryId, ...response };
  } catch (error) {
    toast.error("Subcategory Delete Failed", { autoClose: 3000 });
    return error;
  }
});

// ==================== PRODUCT ATTRIBUTES THUNKS ====================

export const getAttributes = createAsyncThunk("ecommerce/getAttributes", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await getAttributesApi(params);
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const addNewAttribute = createAsyncThunk("ecommerce/addNewAttribute", async (attribute, { dispatch, getState }) => {
  try {
    const response = await addNewAttributeApi(attribute);
    const data = response?.data || response;
    toast.success("Attribute Added Successfully", { autoClose: 3000 });
    
    // Refresh attributes list with proper filters
    const state = getState();
    const userProfile = state.Account?.user || JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getAttributes(params));
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Attribute Add Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const updateAttribute = createAsyncThunk("ecommerce/updateAttribute", async (attribute, { dispatch, getState }) => {
  try {
    const response = await updateAttributeApi(attribute);
    const data = response?.data || response;
    toast.success("Attribute Updated Successfully", { autoClose: 3000 });
    
    // Refresh attributes list with proper filters
    const state = getState();
    const userProfile = state.Account?.user || JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getAttributes(params));
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Attribute Update Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const deleteAttribute = createAsyncThunk("ecommerce/deleteAttribute", async (attributeId, { dispatch, getState }) => {
  try {
    const response = await deleteAttributeApi(attributeId);
    toast.success("Attribute Deleted Successfully", { autoClose: 3000 });
    
    // Refresh attributes list with proper filters
    const state = getState();
    const userProfile = state.Account?.user || JSON.parse(localStorage.getItem("authUser")) || {};
    const userRole = userProfile.role || "admin";
    const userId = userProfile.id || userProfile.uid || userProfile._id;
    const params = userRole === "seller" && userId ? { seller_id: userId } : {};
    dispatch(getAttributes(params));
    
    return { attributeId, ...response };
  } catch (error) {
    toast.error("Attribute Delete Failed", { autoClose: 3000 });
    return error;
  }
});

// ==================== COMMISSIONS THUNKS ====================

export const getCommissions = createAsyncThunk("ecommerce/getCommissions", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await getCommissionsApi();
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const addNewCommission = createAsyncThunk("ecommerce/addNewCommission", async (commission, { dispatch }) => {
  try {
    const response = await addNewCommissionApi(commission);
    const data = response?.data || response;
    toast.success("Commission Added Successfully", { autoClose: 3000 });
    
    // Refresh commissions list
    dispatch(getCommissions());
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Commission Add Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const updateCommission = createAsyncThunk("ecommerce/updateCommission", async (commission, { dispatch }) => {
  try {
    const response = await updateCommissionApi(commission);
    const data = response?.data || response;
    toast.success("Commission Updated Successfully", { autoClose: 3000 });
    
    // Refresh commissions list
    dispatch(getCommissions());
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Commission Update Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const deleteCommission = createAsyncThunk("ecommerce/deleteCommission", async (commissionId, { dispatch, rejectWithValue }) => {
  try {
    await deleteCommissionApi(commissionId);
    toast.success("Commission Deleted Successfully", { autoClose: 3000 });

    // Refresh commissions list so UI updates immediately (no manual refresh needed)
    try {
      await dispatch(getCommissions()).unwrap();
    } catch (refreshErr) {
      // Delete succeeded; refresh failed — reducer will still remove item
    }

    return { commissionId };
  } catch (error) {
    const message = error?.message || error?.error || "Commission delete failed.";
    toast.error(message, { autoClose: 3000 });
    return rejectWithValue(error);
  }
});

// ==================== TAXES THUNKS ====================

export const getTaxes = createAsyncThunk("ecommerce/getTaxes", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await getTaxesApi();
    return response;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const addNewTax = createAsyncThunk("ecommerce/addNewTax", async (tax, { dispatch }) => {
  try {
    const response = await addNewTaxApi(tax);
    const data = response?.data || response;
    toast.success("Tax Added Successfully", { autoClose: 3000 });
    
    // Refresh taxes list
    dispatch(getTaxes());
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Tax Add Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const updateTax = createAsyncThunk("ecommerce/updateTax", async (tax, { dispatch }) => {
  try {
    const response = await updateTaxApi(tax);
    const data = response?.data || response;
    toast.success("Tax Updated Successfully", { autoClose: 3000 });
    
    // Refresh taxes list
    dispatch(getTaxes());
    
    return data;
  } catch (error) {
    const errorMessage = error?.error || error?.message || "Tax Update Failed";
    toast.error(errorMessage, { autoClose: 3000 });
    throw error;
  }
});

export const deleteTax = createAsyncThunk("ecommerce/deleteTax", async (taxId, { dispatch, rejectWithValue }) => {
  try {
    await deleteTaxApi(taxId);
    toast.success("Tax Deleted Successfully", { autoClose: 3000 });

    // Refresh taxes list so UI updates immediately (no manual refresh needed)
    try {
      await dispatch(getTaxes()).unwrap();
    } catch (refreshErr) {
      // Delete succeeded; refresh failed — reducer will still remove item
    }

    return { taxId };
  } catch (error) {
    const message = error?.message || error?.error || "Tax delete failed.";
    toast.error(message, { autoClose: 3000 });
    return rejectWithValue(error);
  }
});
